import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Bot, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { myClientsService } from '@/features/my-clients/services/my-clients.service';
import { getClientDisplayName } from '@/features/clients/utils/display';
import { useAuthStore } from '@/store/auth.store';
import { Permission } from '@/common/enums/permissions.enum';
import { PortfolioAgentDialog } from '@/features/ai-agents/components/chat/PortfolioAgentDialog';

export const Route = createFileRoute('/_authenticated/my-clients/')({
  component: MyClientsPage,
});

function MyClientsPage() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManageAll = hasPermission(Permission.MY_CLIENTS_MANAGE_ALL);
  const canUseAgents = hasPermission(Permission.AI_AGENTS_USE);
  const [attendantUserId, setAttendantUserId] = useState<string>('all');
  const [agentsOpen, setAgentsOpen] = useState(false);

  const { data: filterUsers = [] } = useQuery({
    queryKey: ['my-client-filter-users'],
    queryFn: () => myClientsService.listFilterUsers(),
    enabled: canManageAll,
  });

  const { data: clients = [], isLoading } = useQuery({
    queryKey: [
      'my-clients',
      canManageAll ? attendantUserId : 'own',
    ],
    queryFn: () =>
      myClientsService.list(
        canManageAll && attendantUserId !== 'all'
          ? { attendantUserId }
          : undefined,
      ),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Meus Clientes
          </h1>
          <p className="text-muted-foreground mt-1">
            {canManageAll
              ? 'Todos os clientes ativos do tenant. Filtre por responsável se quiser.'
              : 'Clientes em que você é responsável e o cargo atribuído em cada um.'}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          {canUseAgents && (
            <Button variant="outline" onClick={() => setAgentsOpen(true)}>
              <Bot className="h-4 w-4" />
              Agentes
            </Button>
          )}
          {canManageAll && (
            <div className="w-full sm:w-72 space-y-1.5">
              <label className="text-sm font-medium">Usuário de atendimento</label>
              <Select value={attendantUserId} onValueChange={setAttendantUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os clientes</SelectItem>
                  {filterUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.fullName ? `${u.fullName} (${u.email})` : u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {canUseAgents && (
        <PortfolioAgentDialog
          open={agentsOpen}
          onOpenChange={setAgentsOpen}
          clients={clients.map((c) => ({ id: c.id, name: getClientDisplayName(c) }))}
        />
      )}

      {isLoading ? (
        <div className="text-muted-foreground">Carregando clientes...</div>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {canManageAll
              ? 'Nenhum cliente encontrado para este filtro.'
              : 'Você ainda não está vinculado como responsável em nenhum cliente.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {clients.map((client) => (
            <Link
              key={client.id}
              to="/my-clients/$id"
              params={{ id: client.id }}
              className="block"
            >
              <Card className="hover:bg-muted/40 transition-colors">
                <CardContent className="py-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-medium">
                      {getClientDisplayName(client)}
                    </div>
                    {client.cargo ? (
                      <div className="text-sm text-muted-foreground">
                        Cargo: {client.cargo.name}
                      </div>
                    ) : canManageAll ? (
                      <div className="text-sm text-muted-foreground">
                        Sem vínculo seu neste cliente
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={client.isActive ? 'default' : 'secondary'}>
                      {client.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
